import React, { useState, useEffect } from 'react';
import ApiClient from "../utils/ApiClient";

export default function PoInvoiceCreditList() {
    // State declarations
    const [poData, setPoData] = useState([]);
    const [invoiceData, setInvoiceData] = useState({});
    const [creditData, setCreditData] = useState({});
    const [loading, setLoading] = useState(true);
    const [loadingPOs, setLoadingPOs] = useState({});
    const [loadingInvoices, setLoadingInvoices] = useState({});
    const [error, setError] = useState(null);
    const [expandedPOs, setExpandedPOs] = useState({});
    const [expandedInvoices, setExpandedInvoices] = useState({});

    // Toggle PO expansion and fetch invoice data if needed
    const togglePOExpanded = async (poId, poNumber) => {
        const willBeExpanded = !expandedPOs[poId];

        setExpandedPOs(prev => ({
            ...prev,
            [poId]: willBeExpanded
        }));

        // If expanding and we don't have invoice data yet, fetch it
        if (willBeExpanded && !invoiceData[poNumber]) {
            try {
                setLoadingPOs(prev => ({ ...prev, [poId]: true }));
                const response = await ApiClient.post('/api/pos/polist/invoices', { po_number: poNumber });

                if (response.data && Array.isArray(response.data)) {
                    setInvoiceData(prev => ({
                        ...prev,
                        [poNumber]: response.data
                    }));
                }
            } catch (error) {
                console.error(`Error fetching invoices for PO ${poNumber}:`, error);
                setError(`Failed to load invoices for PO ${poNumber}`);
            } finally {
                setLoadingPOs(prev => ({ ...prev, [poId]: false }));
            }
        }
    };

    // Toggle Invoice expansion and fetch credit data if needed
    const toggleInvoiceExpanded = async (invoiceId, invoiceNumber) => {
        const willBeExpanded = !expandedInvoices[invoiceId];

        setExpandedInvoices(prev => ({
            ...prev,
            [invoiceId]: willBeExpanded
        }));

        // If expanding and we don't have credit data yet, fetch it
        if (willBeExpanded && !creditData[invoiceNumber]) {
            try {
                setLoadingInvoices(prev => ({ ...prev, [invoiceId]: true }));
                const response = await ApiClient.post('/api/pos/credits', { invoice_number: invoiceNumber });

                if (response.data && Array.isArray(response.data)) {
                    setCreditData(prev => ({
                        ...prev,
                        [invoiceNumber]: response.data
                    }));
                }
            } catch (error) {
                console.error(`Error fetching credits for Invoice ${invoiceNumber}:`, error);
                setError(`Failed to load credits for Invoice ${invoiceNumber}`);
            } finally {
                setLoadingInvoices(prev => ({ ...prev, [invoiceId]: false }));
            }
        }
    };

    // Check if a PO is expanded
    const isPOExpanded = (poId) => {
        return !!expandedPOs[poId];
    };

    // Check if an Invoice is expanded
    const isInvoiceExpanded = (invoiceId) => {
        return !!expandedInvoices[invoiceId];
    };

    // Get cell value for a row
    const getValue = (item, columnId) => {
        switch (columnId) {
            case 'po_number':
                return item.poNumber || item.po_number;
            case 'invoice_number':
                return item.invoiceNumber || item.invoice_number;
            case 'credit_number':
                return item.creditNumber || item.credit_number;
            default:
                return item[columnId];
        }
    };

    // Format cell value
    const formatValue = (value, type) => {
        if (value == null) return '-';

        switch (type) {
            case 'number':
                return value.toLocaleString();
            case 'currency':
                return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            case 'date':
                return value ? new Date(value).toLocaleDateString() : '-';
            case 'percentage':
                return `${value}%`;
            default:
                return value;
        }
    };

    // Format percentage with color
    const formatPercentage = (percentage) => {
        if (percentage == null) return <span>-</span>;

        let bgColor, textColor;
        if (percentage === 100) {
            bgColor = 'bg-green-100';
            textColor = 'text-green-800';
        } else if (percentage >= 50) {
            bgColor = 'bg-yellow-100';
            textColor = 'text-yellow-800';
        } else {
            bgColor = 'bg-red-100';
            textColor = 'text-red-800';
        }

        return (
            <span className={`px-2 py-1 rounded-full ${bgColor} ${textColor}`}>
                {percentage}%
            </span>
        );
    };

    // Fetch initial PO summary data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await ApiClient.post('/api/pos/polist');
                if (response.data && Array.isArray(response.data)) {
                    setPoData(response.data);
                } else {
                    throw new Error('Invalid response structure');
                }
            } catch (error) {
                console.error('Error fetching PO summary data:', error);
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Define column headers
    const columns = [
        { id: 'po_number', label: 'PO Number', align: 'left' },
        { id: 'po_date', label: 'PO Date', align: 'left', format: 'date' },
        { id: 'invoice_count', label: 'Invoices', align: 'right', format: 'number' },
        { id: 'credit_count', label: 'Credits', align: 'right', format: 'number' },
        { id: 'qty_ordered', label: 'Qty Ordered', align: 'left' },
        { id: 'qty_received', label: 'Qty Received', align: 'left' },
        { id: 'percent_received', label: '% Received', align: 'left' }

    ];

    // Define invoice columns
    const invoiceColumns = [
        { id: 'invoice_number', label: 'Invoice Number', align: 'left' },
        { id: 'invoice_date', label: 'Invoice Date', align: 'left', format: 'date' },
        { id: 'due_date', label: 'Due Date', align: 'left', format: 'date' },
        { id: 'invoice_amount', label: 'Invoice Amount', align: 'right', format: 'currency' },
        { id: 'credit_amount', label: 'Credit Amount', align: 'right', format: 'currency' },
        { id: 'balance', label: 'Balance', align: 'right', format: 'currency' },
        { id: 'payment_status', label: 'Status', align: 'left' }
    ];

    // Define credit columns
    const creditColumns = [
        { id: 'credit_number', label: 'Credit Number', align: 'left' },
        { id: 'credit_date', label: 'Credit Date', align: 'left', format: 'date' },
        { id: 'credit_amount', label: 'Credit Amount', align: 'right', format: 'currency' },
        { id: 'credit_reason', label: 'Reason', align: 'left' },
        { id: 'applied_amount', label: 'Applied Amount', align: 'right', format: 'currency' },
        { id: 'remaining_amount', label: 'Remaining', align: 'right', format: 'currency' }
    ];

    // Conditional rendering for loading and error states
    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-gray-600">Loading...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="text-lg text-red-600">Error loading data: {error}</div>
            </div>
        );
    }

    return (
        <div className="px-4 sm:px-6 lg:px-8">
            <div className="sm:flex sm:items-center">
                <div className="sm:flex-auto">
                    <h1 className="text-base font-semibold text-gray-900">Purchase Orders / Invoices / Credits</h1>
                    <p className="mt-2 text-sm text-gray-700">
                        A comprehensive list of all POs with their associated Invoices and Credits
                    </p>
                </div>
            </div>
            <div className="mt-8 flow-root">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                    <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead>
                            <tr>
                                {columns.map((column) => (
                                    <th
                                        key={column.id}
                                        className={`py-3.5 text-sm font-semibold text-gray-900 ${
                                            column.align === 'right' ? 'text-right' : 'text-left'
                                        } px-3`}
                                    >
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {poData.map((po, poIndex) => {
                                // Create a unique identifier for each PO row
                                const poId = `po-${poIndex}`;
                                const poNumber = getValue(po, 'po_number');
                                const poExpanded = isPOExpanded(poId);

                                // Prepare unique objects for keys
                                const uniquePoKey = `po-${poIndex}-${poNumber || ''}-${Date.now()}`;

                                return (
                                    <React.Fragment key={uniquePoKey}>
                                        {/* Main PO row */}
                                        <tr
                                            className="hover:bg-gray-100 cursor-pointer"
                                            onClick={() => togglePOExpanded(poId, poNumber)}
                                        >
                                            {columns.map((column) => {
                                                if (column.id === 'po_number') {
                                                    return (
                                                        <td key={`${uniquePoKey}-${column.id}`} className="py-4 text-sm whitespace-nowrap text-left pl-3">
                                                            <div className="flex items-center">
                                                                    <span
                                                                        className="mr-2 cursor-pointer"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            togglePOExpanded(poId, poNumber);
                                                                        }}
                                                                    >
                                                                        {poExpanded ? '▼' : '▶'}
                                                                    </span>
                                                                <span className="text-gray-900 font-medium">
                                                                        {poNumber || '-'}
                                                                    </span>
                                                            </div>
                                                        </td>
                                                    );
                                                } else {
                                                    return (
                                                        <td
                                                            key={`${uniquePoKey}-${column.id}`}
                                                            className={`py-4 text-sm whitespace-nowrap ${
                                                                column.align === 'right' ? 'text-right pr-3' : 'text-left pl-3'
                                                            }`}
                                                        >
                                                                <span className={column.id === 'payment_status' ? `text-${po.payment_status === 'Paid' ? 'green' : 'gray'}-500` : 'text-gray-500'}>
                                                                    {formatValue(getValue(po, column.id), column.format)}
                                                                </span>
                                                        </td>
                                                    );
                                                }
                                            })}
                                        </tr>

                                        {/* Render invoices if PO is expanded */}
                                        {poExpanded && (
                                            <>
                                                {/* Invoice header row */}
                                                <tr className="bg-gray-100">
                                                    {invoiceColumns.map((column, i) => (
                                                        <th
                                                            key={`${uniquePoKey}-invoice-header-${i}`}
                                                            className={`py-2 text-xs font-medium text-gray-700 ${
                                                                column.align === 'right' ? 'text-right pr-3' : 'text-left'
                                                            } ${i === 0 ? 'pl-8' : 'pl-3'}`}
                                                        >
                                                            {column.label}
                                                        </th>
                                                    ))}
                                                    {/* Fill remaining columns */}
                                                    {columns.length > invoiceColumns.length && (
                                                        <th colSpan={columns.length - invoiceColumns.length}></th>
                                                    )}
                                                </tr>

                                                {/* Loading state for invoices */}
                                                {loadingPOs[poId] && (
                                                    <tr className="bg-gray-50">
                                                        <td colSpan={Math.max(columns.length, invoiceColumns.length)} className="py-4 text-sm text-center text-gray-500">
                                                            Loading invoices...
                                                        </td>
                                                    </tr>
                                                )}

                                                {/* Render invoices if available */}
                                                {!loadingPOs[poId] && invoiceData[poNumber] && (
                                                    invoiceData[poNumber].length > 0 ? (
                                                        invoiceData[poNumber].map((invoice, invoiceIndex) => {
                                                            const invoiceId = `${poId}-invoice-${invoiceIndex}`;
                                                            const invoiceExpanded = isInvoiceExpanded(invoiceId);
                                                            const invoiceNumber = getValue(invoice, 'invoice_number');
                                                            const hasCredits = invoice.credit_count > 0;

                                                            const uniqueInvoiceKey = `${uniquePoKey}-invoice-${invoiceIndex}-${invoiceNumber || ''}-${Date.now()}`;

                                                            return (
                                                                <React.Fragment key={uniqueInvoiceKey}>
                                                                    {/* Invoice row */}
                                                                    <tr
                                                                        className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                                                                        onClick={() => toggleInvoiceExpanded(invoiceId, invoiceNumber)}
                                                                    >
                                                                        {invoiceColumns.map((column, i) => {
                                                                            if (column.id === 'invoice_number') {
                                                                                return (
                                                                                    <td key={`${uniqueInvoiceKey}-${column.id}`} className="py-3 text-sm whitespace-nowrap text-left pl-8">
                                                                                        <div className="flex items-center">
                                                                                                <span
                                                                                                    className="mr-2 cursor-pointer"
                                                                                                    onClick={(e) => {
                                                                                                        e.stopPropagation();
                                                                                                        toggleInvoiceExpanded(invoiceId, invoiceNumber);
                                                                                                    }}
                                                                                                >
                                                                                                    {invoiceExpanded ? '▼' : '▶'}
                                                                                                </span>
                                                                                            <span className="text-gray-800">
                                                                                                    {invoiceNumber || '-'}
                                                                                                </span>
                                                                                        </div>
                                                                                    </td>
                                                                                );
                                                                            } else {
                                                                                return (
                                                                                    <td
                                                                                        key={`${uniqueInvoiceKey}-${column.id}`}
                                                                                        className={`py-3 text-sm whitespace-nowrap ${
                                                                                            column.align === 'right' ? 'text-right pr-3' : 'text-left pl-3'
                                                                                        }`}
                                                                                    >
                                                                                            <span className="text-gray-600">
                                                                                                {formatValue(getValue(invoice, column.id), column.format)}
                                                                                            </span>
                                                                                    </td>
                                                                                );
                                                                            }
                                                                        })}
                                                                        {/* Fill remaining columns */}
                                                                        {columns.length > invoiceColumns.length && (
                                                                            <td colSpan={columns.length - invoiceColumns.length}></td>
                                                                        )}
                                                                    </tr>

                                                                    {/* Render credits if invoice is expanded */}
                                                                    {invoiceExpanded && (
                                                                        <>
                                                                            {/* Credit header row */}
                                                                            <tr className="bg-gray-200">
                                                                                {creditColumns.map((column, i) => (
                                                                                    <th
                                                                                        key={`${uniqueInvoiceKey}-credit-header-${i}`}
                                                                                        className={`py-2 text-xs font-medium text-gray-600 ${
                                                                                            column.align === 'right' ? 'text-right pr-3' : 'text-left'
                                                                                        } ${i === 0 ? 'pl-12' : 'pl-3'}`}
                                                                                    >
                                                                                        {column.label}
                                                                                    </th>
                                                                                ))}
                                                                                {/* Fill remaining columns */}
                                                                                {columns.length > creditColumns.length && (
                                                                                    <th colSpan={columns.length - creditColumns.length}></th>
                                                                                )}
                                                                            </tr>

                                                                            {/* Loading state for credits */}
                                                                            {loadingInvoices[invoiceId] && (
                                                                                <tr className="bg-gray-50">
                                                                                    <td colSpan={Math.max(columns.length, creditColumns.length)} className="py-3 text-sm text-center text-gray-500">
                                                                                        Loading credits...
                                                                                    </td>
                                                                                </tr>
                                                                            )}

                                                                            {/* Render credits if available */}
                                                                            {!loadingInvoices[invoiceId] && (
                                                                                creditData[invoiceNumber] && creditData[invoiceNumber].length > 0 ? (
                                                                                    creditData[invoiceNumber].map((credit, creditIndex) => {
                                                                                        const uniqueCreditKey = `${uniqueInvoiceKey}-credit-${creditIndex}-${getValue(credit, 'credit_number') || ''}-${Date.now()}`;

                                                                                        return (
                                                                                            <tr key={uniqueCreditKey} className="bg-gray-50">
                                                                                                {creditColumns.map((column, i) => {
                                                                                                    return (
                                                                                                        <td
                                                                                                            key={`${uniqueCreditKey}-${column.id}`}
                                                                                                            className={`py-3 text-sm whitespace-nowrap ${
                                                                                                                column.align === 'right' ? 'text-right pr-3' : 'text-left'
                                                                                                            } ${i === 0 ? 'pl-12' : 'pl-3'}`}
                                                                                                        >
                                                                                                                <span className="text-gray-600">
                                                                                                                    {formatValue(getValue(credit, column.id), column.format)}
                                                                                                                </span>
                                                                                                        </td>
                                                                                                    );
                                                                                                })}
                                                                                                {/* Fill remaining columns */}
                                                                                                {columns.length > creditColumns.length && (
                                                                                                    <td colSpan={columns.length - creditColumns.length}></td>
                                                                                                )}
                                                                                            </tr>
                                                                                        );
                                                                                    })
                                                                                ) : (
                                                                                    <tr className="bg-gray-50">
                                                                                        <td colSpan={Math.max(columns.length, creditColumns.length)} className="py-3 text-sm text-center text-gray-500">
                                                                                            No credits available for this invoice
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            )}
                                                                        </>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })
                                                    ) : (
                                                        <tr className="bg-gray-50">
                                                            <td colSpan={Math.max(columns.length, invoiceColumns.length)} className="py-4 text-sm text-center text-gray-500">
                                                                No invoices available for this PO
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                                {!loadingPOs[poId] && !invoiceData[poNumber] && (
                                                    <tr className="bg-gray-50">
                                                        <td colSpan={Math.max(columns.length, invoiceColumns.length)} className="py-4 text-sm text-center text-gray-500">
                                                            No invoice data found for this PO
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}